import {
    PropsWithChildren,
    useState,
    useRef,
    useEffect,
    ReactNode
} from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ChevronRightLargeIcon from "@atlaskit/icon/glyph/chevron-right-large";
import { B300, N600 } from "@atlaskit/theme/colors";

const buttonCss = css({
    "all": "initial",
    "&:focus-visible": {
        outlineColor: B300,
        outlineStyle: "auto",
        outlineWidth: "1px"
    }
});

const labelContentsCss = css({
    fontFamily: "Charlie Text !important",
    fontSize: "0.875rem",
    lineHeight: "normal",
    color: `${N600} !important`,
    padding: "0 !important"
});

const buttonContentCss = css({
    display: "flex",
    gap: "4px"
});

export interface ExpandableContentProps extends PropsWithChildren {
    eventDataName: string;
    label: ReactNode;
    labelCss?: React.CSSProperties;
    expandedLabelText?: ReactNode;
}

export const ExpandableContent = (props: ExpandableContentProps) => {
    const [expanded, setExpanded] = useState(false);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    // contentRef controls the content element height upon expansion change
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!contentRef.current) {
            return;
        }
        if (expanded) {
            contentRef.current.style.maxHeight = `${contentRef.current.scrollHeight}px`;
        } else {
            contentRef.current.style.maxHeight = "0";
        }
    }, [expanded]);

    const handleButtonClick = () => {
        if (props.eventDataName) {
            const data = {
                name: props.eventDataName,
                direction: expanded ? "close" : "open"
            };
            createAnalyticsEvent({
                eventName: "expandableContentClicked",
                eventType: "ui",
                eventComponent: "button",
                event: "clicked",
                actionSubject: "expandableContent",
                action: "clicked",
                attributes: data,
                data
            }).fire();
        }

        setExpanded((prev) => !prev);
    };

    const labelContents = (
        <span css={buttonContentCss}>
            <span
                css={css({
                    display: "inline-block",
                    height: "24px",
                    width: "24px",
                    transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.1s ease-in-out"
                })}
            >
                {props.children && (
                    <ChevronRightLargeIcon label="" size="medium" />
                )}
            </span>
            <span css={css({ paddingBlockEnd: 2 })} style={props.labelCss}>
                {expanded && props.expandedLabelText
                    ? props.expandedLabelText
                    : props.label}
            </span>
        </span>
    );

    return (
        <div>
            {props.children ? (
                <button
                    css={[buttonCss, labelContentsCss]}
                    style={{ cursor: "pointer" }}
                    onClick={handleButtonClick}
                >
                    {labelContents}
                </button>
            ) : (
                <span css={labelContentsCss}>{labelContents}</span>
            )}

            <div
                ref={contentRef}
                css={css({
                    display: "block !important",
                    overflow: "hidden",
                    transition: "max-height 0.15s ease-in-out",
                    paddingInlineStart: "28px"
                })}
                aria-hidden={!expanded}
                tabIndex={expanded ? 0 : -1}
                role={expanded ? "region" : undefined}
            >
                {props.children}
            </div>
        </div>
    );
};
